import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { formatCentsIntl, fetchConfig } from '@theme/utilities';

// How long the "Added to bag" state shows before the button returns to its normal label
const ADDED_STATE_DURATION = 2500;

// Safety net: product-form.js always dispatches cart:update or cart:error, but a network failure
// dispatches neither, so don't leave the button stuck in its pending state.
const PENDING_FALLBACK_DURATION = 10000;

// Matches the theme's mobile breakpoint
const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

/**
 * One matching-set product in the "Style it with" block (snippets/style-it-with.liquid).
 *
 * Owns size selection and the button label only. The add goes through the nested
 * product-form-component, so cart drawer, cart count, errors and the live region behave
 * exactly like the main buy buttons. Selecting a size here never touches the main
 * variant picker, URL or price: these radios aren't inside it and fire no variant events.
 *
 * @extends {Component}
 */
class StyleItWithComponent extends Component {
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #addedTimeout;

  /** @type {number | undefined} */
  #pendingTimeout;

  /** Tag-based promo percentage from the nested promo-banner, e.g. 20 for FULL PRICE. */
  /** @type {number | null} */
  #promoPercent = null;

  /** @type {{ sourceEl: Element, currency?: string, locale?: string } | null} */
  #promoFormat = null;

  /** The main product's size when this item's size was filled in from it; '' once the customer picks their own. */
  #matchedSize = '';

  /**
   * True once the customer picks a size in this item themselves. From then on the main product's size never
   * overrides it, so a set in two sizes (e.g. top S, skirt M) stays as chosen.
   */
  #chosenByCustomer = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Capture phase so a click with no size selected is stopped before the form submits.
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    // Both bubble up from the nested product-form-component.
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartAdd, { signal });
    this.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });
    // Swap between the short and long "add size" label when crossing the mobile breakpoint.
    MOBILE_QUERY.addEventListener('change', () => this.#render(), { signal });
    // Dispatched by the promo-banner inside this item's price (snippets/price.liquid).
    this.addEventListener('promo-discount-ready', this.#onPromoReady, { signal });
    // Mirror the size picked in the main variant picker.
    document.addEventListener('change', this.#onMainSizeChange, { signal });

    this.#render();

    // A size can already be chosen on load, e.g. from a ?variant= link.
    const mainSize = this.#getMainSize();
    if (mainSize) this.#selectMatchingSize(mainSize);

    // The banner may have dispatched before this module loaded. Ask again; product-pricing-discount.js
    // skips re-rendering a promo it has already applied, so this only fills in the button label.
    const promoBanner = /** @type {any} */ (this.querySelector('promo-banner'));
    promoBanner?.triggerDiscountEvent?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    clearTimeout(this.#addedTimeout);
    clearTimeout(this.#pendingTimeout);
  }

  /** Called from the size radios via on:change, i.e. the customer picked a size themselves. */
  selectSize() {
    this.#matchedSize = '';
    this.#chosenByCustomer = true;
    this.classList.remove('style-it-with__item--needs-size');
    this.#render();

    const selected = this.#selectedSize;
    if (selected) this.#fillMainSizeIfEmpty(selected.value);
  }

  /**
   * What the "Add both" button (StyleItWithSet) needs from this item.
   * @returns {{ status: 'ready' | 'needs-size' | 'unavailable', variantId: string }}
   */
  getSetSelection() {
    const selected = this.#selectedSize;
    if (selected) {
      const variantId = selected.dataset.optionAvailable === 'false' ? '' : selected.dataset.variantId || '';
      return { status: variantId ? 'ready' : 'unavailable', variantId };
    }

    // Single-variant products, or products without a Size option, have nothing to pick.
    const { defaultVariantId } = this.dataset;
    if (defaultVariantId) return { status: 'ready', variantId: defaultVariantId };

    return { status: 'needs-size', variantId: '' };
  }

  /** Highlights this item's sizes and focuses the first available one. */
  promptForSize() {
    this.classList.add('style-it-with__item--needs-size');
    this.#setLabel(this.dataset.textPickSize);

    const firstAvailable = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:not([data-option-available="false"])')
    );
    firstAvailable?.focus();
  }

  /**
   * With an "Add both" button in the panel, this item's add is a small "or add this piece only" text
   * link (snippets/style-it-with.liquid), shown only when there's something to add.
   */
  get #isLinkMode() {
    return this.#button.classList.contains('style-it-with__only-link');
  }

  get #button() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('[data-style-it-with-button]'));
  }

  get #variantInput() {
    return /** @type {HTMLInputElement} */ (this.querySelector('form input[name="id"]'));
  }

  get #selectedSize() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
  }

  /**
   * Selectors for the page product's Size control in the main variant picker: button pills
   * (data-input-id is "<option position>-<index>") or the dropdown (name="options[Size]").
   * @returns {{ radios: string, select: string } | null}
   */
  get #mainSizeSelectors() {
    const panel = /** @type {HTMLElement | null} */ (this.closest('[data-style-it-with]'));
    const { productId, sizePosition, sizeOptionName } = panel?.dataset ?? {};
    if (!productId || !sizePosition || !sizeOptionName) return null;

    const picker = `variant-picker[data-product-id="${productId}"]`;
    return {
      radios: `${picker} input[type="radio"][data-input-id^="${sizePosition}-"]`,
      select: `${picker} select[name="options[${CSS.escape(sizeOptionName)}]"]`,
    };
  }

  /** @returns {string} The size currently chosen in the main variant picker, or '' if none. */
  #getMainSize() {
    const selectors = this.#mainSizeSelectors;
    if (!selectors) return '';

    const radio = /** @type {HTMLInputElement | null} */ (document.querySelector(`${selectors.radios}:checked`));
    const select = /** @type {HTMLSelectElement | null} */ (document.querySelector(selectors.select));
    return radio?.value || select?.value || '';
  }

  /**
   * @param {Event} event
   */
  #onMainSizeChange = (event) => {
    const selectors = this.#mainSizeSelectors;
    const target = /** @type {Element} */ (event.target);
    if (!selectors || !target.matches?.(`${selectors.radios}, ${selectors.select}`)) return;

    const value = /** @type {HTMLInputElement | HTMLSelectElement} */ (target).value;
    if (value) this.#selectMatchingSize(value);
  };

  /**
   * Fill-empty-only, the other way: when the customer picks a size here before choosing one on the main
   * product, give the main product the same size so "Add both" can appear. A size already chosen on the main
   * product is never changed, and a size the main product is sold out in isn't filled.
   *
   * Clicks the main picker's own pill (or sets its dropdown and fires change), so the main product reacts
   * exactly as if the customer had picked it there: price, URL, add to cart, Afterpay and so on.
   * @param {string} size
   */
  #fillMainSizeIfEmpty(size) {
    const selectors = this.#mainSizeSelectors;
    if (!selectors || this.#getMainSize()) return;

    const wanted = size.trim().toLowerCase();
    const radio = /** @type {HTMLInputElement[]} */ ([...document.querySelectorAll(selectors.radios)]).find(
      (input) => input.value.trim().toLowerCase() === wanted
    );
    if (radio) {
      if (radio.dataset.optionAvailable !== 'false') radio.click();
      return;
    }

    const select = /** @type {HTMLSelectElement | null} */ (document.querySelector(selectors.select));
    const option = select && [...select.options].find((item) => !item.disabled && item.value.trim().toLowerCase() === wanted);
    if (select && option) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Selects the same size in this item, unless the customer has already picked a size here themselves.
   * If this product doesn't come in that size, clears the selection so the customer picks one rather than
   * adding a size they didn't choose. A sold-out size is still selected, so "Add both" shows it's unavailable.
   * @param {string} size
   */
  #selectMatchingSize(size) {
    if (this.#chosenByCustomer && this.#selectedSize) return;

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'));
    if (!radios.length) return;

    const wanted = size.trim().toLowerCase();
    let found = false;
    for (const radio of radios) {
      radio.checked = radio.value.trim().toLowerCase() === wanted;
      found ||= radio.checked;
    }

    this.#matchedSize = found ? size : '';
    this.classList.remove('style-it-with__item--needs-size');
    this.#render();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!target.closest('[data-style-it-with-button]')) return;

    const button = this.#button;

    if (button.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }

    if (!this.#variantInput.value) {
      event.preventDefault();
      this.promptForSize();
      return;
    }

    // Let the click submit the form; product-form-component handles the add.
    button.setAttribute('aria-busy', 'true');
    clearTimeout(this.#pendingTimeout);
    this.#pendingTimeout = setTimeout(() => this.#clearPending(), PENDING_FALLBACK_DURATION);
  };

  /**
   * @param {Event & {detail?: {data?: {didError?: boolean}}}} event
   */
  #onCartAdd = (event) => {
    this.#clearPending();

    if (event.detail?.data?.didError) {
      this.#render();
      return;
    }

    this.#setLabel(this.dataset.textAdded, true);

    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => this.#render(), ADDED_STATE_DURATION);
  };

  /**
   * Records the promo so the button label shows the same discounted price as the price above it.
   * @param {CustomEvent<{ discountedPrice?: number }>} event
   */
  #onPromoReady = (event) => {
    const promoBanner = /** @type {any} */ (event.target);
    const props = promoBanner?.props || {};
    const basePrice = Number(props.productPrice) || 0;
    const discountedPrice = Number(event.detail?.discountedPrice) || 0;
    if (!basePrice || !discountedPrice || discountedPrice >= basePrice) return;

    this.#promoPercent = Math.round((1 - discountedPrice / basePrice) * 100);
    this.#promoFormat = { sourceEl: promoBanner, currency: props.currencyCode, locale: props.locale };
    this.#render();
  };

  /**
   * @param {string | undefined} cents - Variant price in cents
   * @param {string | undefined} formatted - The Liquid-formatted price, used when there's no promo
   * @returns {string}
   */
  #formatPrice(cents, formatted) {
    const priceCents = Number(cents) || 0;
    if (this.#promoPercent === null || !this.#promoFormat || !priceCents) return formatted ?? '';

    // Same formula as promo-banner.js so the label matches the discounted price shown above it.
    const discountedCents = Math.max(0, Math.ceil(priceCents * (1 - this.#promoPercent / 100)));
    return formatCentsIntl(discountedCents, this.#promoFormat);
  }

  #onCartError = () => {
    this.#clearPending();
    this.#render();
  };

  #clearPending() {
    clearTimeout(this.#pendingTimeout);
    this.#button.removeAttribute('aria-busy');
  }

  /**
   * Syncs the hidden variant input, the size note and the button (or link) with the current selection.
   */
  #render() {
    const button = this.#button;
    const selected = this.#selectedSize;
    const { defaultVariantId, defaultPrice } = this.dataset;

    let variantId = '';
    let price = '';
    let disabled = false;

    if (selected) {
      if (selected.dataset.optionAvailable === 'false' || !selected.dataset.variantId) {
        disabled = true;
      } else {
        variantId = selected.dataset.variantId;
        price = this.#formatPrice(selected.dataset.priceCents, selected.dataset.price);
      }
    } else if (defaultVariantId) {
      variantId = defaultVariantId;
      price = this.#formatPrice(this.dataset.defaultPriceCents, defaultPrice);
    }

    this.#variantInput.value = variantId;
    button.disabled = disabled;
    this.#renderMatchedNote(selected);

    if (this.#isLinkMode) {
      // "Add both" covers the other states (pick a size, sold out), so the link only appears when it can add.
      button.hidden = !variantId;
      this.#setLabel(variantId ? `${this.dataset.textAddOnly} · ${price}` : '');
      return;
    }

    let label = this.dataset.textSelectSize;
    if (disabled) {
      label = this.dataset.textSoldOut;
    } else if (selected) {
      // The details column is ~210px on mobile, so use the short label there to stop it wrapping.
      const template = MOBILE_QUERY.matches
        ? this.dataset.textAddSizeMobile || this.dataset.textAddSize
        : this.dataset.textAddSize;
      label = `${(template || '').replace('[size]', selected.value)} · ${price}`;
    } else if (defaultVariantId) {
      label = `${this.dataset.textAddDefault} · ${price}`;
    }

    // Outlined until there's something to add, then solid like the main add to cart.
    button.classList.toggle('button-secondary', !variantId);
    this.#setLabel(label);
  }

  /**
   * "Matched to your size (M)": shown while the selected size is the one copied from the main product,
   * so the customer knows it was picked for them and can change it.
   * @param {HTMLInputElement | null} selected
   */
  #renderMatchedNote(selected) {
    const note = /** @type {HTMLElement | null} */ (this.querySelector('.style-it-with__matched'));
    if (!note) return;

    const show = Boolean(this.#matchedSize && selected);
    note.hidden = !show;

    const text = note.querySelector('.style-it-with__matched-text');
    if (show && text && selected) text.textContent = (this.dataset.textMatched || '').replace('[size]', selected.value);
  }

  /**
   * @param {string | undefined} text
   * @param {boolean} [showIcon]
   */
  #setLabel(text, showIcon = false) {
    const textElement = this.#button.querySelector('.style-it-with__button-text');
    const icon = /** @type {HTMLElement | null} */ (this.#button.querySelector('.style-it-with__button-icon'));

    if (textElement) textElement.textContent = text ?? '';
    if (icon) icon.hidden = !showIcon;
  }
}

if (!customElements.get('style-it-with-component')) {
  customElements.define('style-it-with-component', StyleItWithComponent);
}

/**
 * Reads the props a promo-banner was rendered with, even before its module has upgraded it.
 * @param {any} promoBanner
 * @returns {{ productPrice?: number, currencyCode?: string, locale?: string }}
 */
function getPromoProps(promoBanner) {
  if (promoBanner?.props) return promoBanner.props;
  try {
    return JSON.parse(promoBanner?.getAttribute('props') || '{}');
  } catch {
    return {};
  }
}

/**
 * The set total in the "Style it with" label: the page product plus each match shown.
 *
 * Applies the same tag-based promos as the prices on the page. Each product's promo-banner
 * (snippets/price.liquid) reports its discount, and the total uses the same formula as
 * promo-banner.js so it matches the discounted prices shown. Also follows the main product's
 * variant changes.
 */
class StyleItWithTotal extends HTMLElement {
  #abortController = new AbortController();

  /** Base price in cents per product id, from data-set-items. */
  /** @type {Map<string, number>} */
  #prices = new Map();

  /** Promo percentage per product id, e.g. 20 for FULL PRICE. */
  /** @type {Map<string, number>} */
  #promoPercents = new Map();

  /** @type {{ sourceEl: Element, currency?: string, locale?: string } | null} */
  #promoFormat = null;

  connectedCallback() {
    for (const entry of (this.dataset.setItems || '').split(',')) {
      const [id, cents] = entry.split(':');
      if (id && Number(cents)) this.#prices.set(id, Number(cents));
    }

    const { signal } = this.#abortController;
    // Every promo-banner bubbles this up, so one listener covers the main price and each match.
    document.addEventListener('promo-discount-ready', this.#onPromoReady, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Banners that dispatched before this module loaded have already been applied by
    // product-pricing-discount.js, which stores the result on their product-price.
    for (const id of this.#prices.keys()) {
      const productPrice = /** @type {HTMLElement | null} */ (
        document.querySelector(`product-price[data-product-id="${id}"][data-promo-discounted-cents]`)
      );
      const promoBanner = productPrice?.querySelector('promo-banner');
      if (productPrice && promoBanner) {
        this.#recordPromo(id, promoBanner, Number(productPrice.dataset.promoDiscountedCents));
      }
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * @param {CustomEvent<{ discountedPrice?: number }>} event
   */
  #onPromoReady = (event) => {
    const promoBanner = /** @type {Element} */ (event.target);
    const id = /** @type {HTMLElement | null} */ (promoBanner.closest?.('product-price'))?.dataset.productId;
    if (!id || !this.#prices.has(id)) return;

    this.#recordPromo(id, promoBanner, Number(event.detail?.discountedPrice));
  };

  /**
   * @param {Event & { detail?: { resource?: { price?: number }, data?: { productId?: string } } }} event
   */
  #onVariantUpdate = (event) => {
    const id = String(event.detail?.data?.productId ?? '');
    const price = Number(event.detail?.resource?.price);
    if (!this.#prices.has(id) || !price || price === this.#prices.get(id)) return;

    this.#prices.set(id, price);
    this.#render();
  };

  /**
   * @param {string} id - Product id
   * @param {Element} promoBanner
   * @param {number} discountedPrice - Discounted price in cents
   */
  #recordPromo(id, promoBanner, discountedPrice) {
    const props = getPromoProps(promoBanner);
    const basePrice = Number(props.productPrice) || 0;
    if (!basePrice || !discountedPrice || discountedPrice >= basePrice) return;

    this.#promoPercents.set(id, Math.round((1 - discountedPrice / basePrice) * 100));
    this.#promoFormat = { sourceEl: promoBanner, currency: props.currencyCode, locale: props.locale };
    this.#render();
  }

  #render() {
    let total = 0;
    for (const [id, cents] of this.#prices) {
      const percent = this.#promoPercents.get(id);
      // Same formula as promo-banner.js so the total matches the discounted prices shown.
      total += percent === undefined ? cents : Math.max(0, Math.ceil(cents * (1 - percent / 100)));
    }

    const totalElement = this.querySelector('[data-set-total]');
    if (totalElement) totalElement.textContent = formatCentsIntl(total, this.#promoFormat ?? {});
  }
}

if (!customElements.get('style-it-with-total')) {
  customElements.define('style-it-with-total', StyleItWithTotal);
}

/**
 * @typedef {{ status: 'ready' | 'needs-size' | 'unavailable', variantId: string }} SetSelection
 * @typedef {'needs-main' | 'needs-items' | 'unavailable' | 'ready'} SetStatus
 */

/**
 * "Add both" button (snippets/style-it-with.liquid): adds the page product plus every item in the
 * panel in one /cart/add request.
 *
 * Hidden until the customer chooses a size on the main product; until then the item buttons already
 * say "Select a size". After that the label says what's needed next: a size for each item (usually
 * filled in already, since items mirror the main size), then "Add both". Clicking early guides them
 * to the missing size instead of failing. After adding it fires the same
 * cart:update event as the theme's product forms, so the cart drawer, count and cart sections refresh
 * as for any other add.
 */
class StyleItWithSet extends HTMLElement {
  #abortController = new AbortController();

  #busy = false;

  /** @type {number | undefined} */
  #addedTimeout;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.#button.addEventListener('click', this.#onClick, { signal });
    // Item size pills bubble up here; the main picker's size and variant changes fire on document.
    document.addEventListener('change', this.#queueRender, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#queueRender, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    clearTimeout(this.#addedTimeout);
  }

  get #button() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('.style-it-with__set-button'));
  }

  get #panel() {
    return /** @type {HTMLElement | null} */ (this.closest('[data-style-it-with]'));
  }

  get #items() {
    return /** @type {StyleItWithComponent[]} */ ([...(this.#panel?.querySelectorAll('style-it-with-component') ?? [])]);
  }

  /** The page product's main variant picker. */
  get #mainPicker() {
    const productId = this.#panel?.dataset.productId;
    return productId ? document.querySelector(`variant-picker[data-product-id="${productId}"]`) : null;
  }

  /**
   * The page product's chosen variant. With a Size option, reads the main picker's size pills or
   * dropdown; otherwise the main product form's variant, as there's nothing for the customer to pick.
   * @returns {SetSelection}
   */
  #getMainSelection() {
    const panel = this.#panel;
    const { productId, sizePosition, sizeOptionName } = panel?.dataset ?? {};
    const picker = this.#mainPicker;

    if (picker && sizePosition) {
      const radios = /** @type {HTMLInputElement[]} */ ([
        ...picker.querySelectorAll(`input[type="radio"][data-input-id^="${sizePosition}-"]`),
      ]);
      const select = sizeOptionName
        ? /** @type {HTMLSelectElement | null} */ (
            picker.querySelector(`select[name="options[${CSS.escape(sizeOptionName)}]"]`)
          )
        : null;
      const checked = radios.find((radio) => radio.checked);

      if ((radios.length && !checked) || (select && !select.value)) {
        return { status: 'needs-size', variantId: '' };
      }

      if (checked?.dataset.optionAvailable === 'false') return { status: 'unavailable', variantId: '' };
      if (checked?.dataset.variantId) return { status: 'ready', variantId: checked.dataset.variantId };
    }

    const mainForm = document.querySelector(
      `product-form-component[data-product-id="${productId}"]:not([data-ignore-variant-events="true"])`
    );
    const variantId = /** @type {HTMLInputElement | null} */ (mainForm?.querySelector('input[name="id"]'))?.value;
    return variantId ? { status: 'ready', variantId } : { status: 'needs-size', variantId: '' };
  }

  /**
   * @returns {{ status: SetStatus, variantIds: string[], itemNeedingSize?: StyleItWithComponent }}
   */
  #getState() {
    const main = this.#getMainSelection();
    const items = this.#items.map((item) => ({ item, ...item.getSetSelection() }));

    if (main.status === 'needs-size') return { status: 'needs-main', variantIds: [] };
    if (main.status === 'unavailable' || items.some((entry) => entry.status === 'unavailable')) {
      return { status: 'unavailable', variantIds: [] };
    }

    const itemNeedingSize = items.find((entry) => entry.status === 'needs-size')?.item;
    if (itemNeedingSize) return { status: 'needs-items', variantIds: [], itemNeedingSize };

    return { status: 'ready', variantIds: [main.variantId, ...items.map((entry) => entry.variantId)] };
  }

  // Wait a frame so the items' own size-mirroring handlers have run first.
  #queueRender = () => requestAnimationFrame(() => this.#render());

  #render() {
    if (this.#busy || this.#addedTimeout !== undefined) return;

    const { status } = this.#getState();

    this.hidden = status === 'needs-main';
    if (status === 'needs-main') return;

    const labels = {
      'needs-items': this.dataset.textSelectItems,
      unavailable: this.dataset.textUnavailable,
      ready: this.dataset.textAdd,
    };

    const button = this.#button;
    button.disabled = status === 'unavailable';
    // Outlined until both items can be added, then solid like the main add to cart.
    button.classList.toggle('button-secondary', status !== 'ready');
    this.#setLabel(labels[status], status === 'ready');
  }

  #onClick = () => {
    if (this.#busy) return;

    const state = this.#getState();
    if (state.status === 'needs-items') return state.itemNeedingSize?.promptForSize();
    if (state.status === 'ready') this.#add(state.variantIds);
  };

  /**
   * @param {string[]} variantIds
   */
  async #add(variantIds) {
    const button = this.#button;
    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    this.#setError('');

    // Cart sections on the page (e.g. the cart drawer), re-rendered from the response like product-form.js.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    /** @type {{ items: object[], sections?: string }} */
    const body = {
      // Hidden property (underscore prefix) so orders show these were added with the "Add both" button.
      items: variantIds.map((id) => ({ id: Number(id), quantity: 1, properties: { _source: 'style-it-with-set' } })),
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    let added = false;
    try {
      const cartAddUrl = /** @type {any} */ (window).Theme?.routes?.cart_add_url ?? '/cart/add';
      const response = await fetch(cartAddUrl, fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();

      if (data.status) {
        // Shopify's message, e.g. that a size has sold out or the bag already holds the maximum.
        this.#setError(data.description || data.message);
        return;
      }

      added = true;
      this.dispatchEvent(
        new CartAddEvent({}, 'style-it-with-set', {
          source: 'product-form-component',
          itemCount: variantIds.length,
          productId: this.#panel?.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#setError('Something went wrong. Please try again.');
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');

      if (added) {
        this.#setLabel(this.dataset.textAdded);
        this.#addedTimeout = setTimeout(() => {
          this.#addedTimeout = undefined;
          this.#render();
        }, ADDED_STATE_DURATION);
      } else {
        this.#render();
      }
    }
  }

  /**
   * @param {string | undefined} text
   * @param {boolean} [showPrice] - Show the set total after the label ("Add both to bag · $160")
   */
  #setLabel(text, showPrice = false) {
    const textElement = this.#button.querySelector('.style-it-with__set-button-text');
    const priceElement = /** @type {HTMLElement | null} */ (this.#button.querySelector('.style-it-with__set-price'));

    if (textElement) textElement.textContent = text ?? '';
    if (priceElement) priceElement.hidden = !showPrice;
  }

  /**
   * @param {string} message - Empty to clear
   */
  #setError(message) {
    const errorElement = /** @type {HTMLElement | null} */ (this.querySelector('.style-it-with__set-error'));
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.hidden = !message;
  }
}

if (!customElements.get('style-it-with-set')) {
  customElements.define('style-it-with-set', StyleItWithSet);
}
